import {
  type UseQueryResult,
  type UseSuspenseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import type { Column, ColumnDef } from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select';
import { Spinner } from './spinner';
import { Tooltip, TooltipContent, TooltipTrigger } from './tooltip';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    /** Fixa a coluna nessa borda da tabela, sempre visível ao rolar horizontalmente. */
    sticky?: 'left' | 'right';
  }
}

const STICKY_COLUMN_CLASSNAME: Record<'left' | 'right', string> = {
  left: 'sticky left-0 z-10 bg-background',
  right: 'sticky right-0 z-10 bg-background',
};

type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

type AnyFetcher = (
  ...args: any[]
) => UseSuspenseQueryResult<any, any> | UseQueryResult<any, any>;

export type InferFetcherParams<TFetcher extends AnyFetcher> = TFetcher extends (
  params?: infer P,
) => any
  ? NonNullable<P>
  : never;

export type InferFetcherResponse<TFetcher extends AnyFetcher> = NonNullable<
  ReturnType<TFetcher>['data']
>;

export type InferRow<TFetcher extends AnyFetcher> =
  InferFetcherResponse<TFetcher> extends {
    data: (infer Row)[];
  }
    ? Prettify<Row>
    : never;

interface DataTableBaseProps<TRow> {
  columns: ColumnDef<TRow>[];
  pageSize?: number;
  onRowDoubleClick?: (row: TRow) => void;
  onRowClick?: (row: TRow) => void;
  defaultSort?: {
    id: keyof TRow;
    desc: boolean;
  };
}

export type DataTableRemoteProps<
  TFetcher extends AnyFetcher,
  TRow = InferRow<TFetcher>,
> = Prettify<
  DataTableBaseProps<TRow> & {
    useFetcher: TFetcher;
    search?: Partial<InferFetcherParams<TFetcher>>;
    data?: never;
  }
>;

export type DataTableLocalProps<TRow> = Prettify<
  DataTableBaseProps<TRow> & {
    data: TRow[];
    useFetcher?: never;
    search?: never;
  }
>;

export type DataTableProps<TFetcher extends AnyFetcher, TRow = InferRow<TFetcher>> =
  | DataTableRemoteProps<TFetcher, TRow>
  | DataTableLocalProps<TRow>;

function useStaticFetcher() {
  return { data: undefined, isLoading: false, isFetching: false } as any;
}

export function DataTable<TFetcher extends AnyFetcher = any, TRow = InferRow<TFetcher>>(
  props: DataTableProps<TFetcher, TRow>,
) {
  const { columns, pageSize = 10, onRowDoubleClick, onRowClick, defaultSort } = props;

  const isLocal = 'data' in props && props.data !== undefined;

  const search = !isLocal && 'search' in props ? (props.search ?? {}) : {};

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: (defaultSort?.id as string) ?? 'createdAt',
      desc: defaultSort?.desc ?? true,
    },
  ]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = useState({});

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize,
  });

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));
  }, [JSON.stringify(search)]);

  const useActiveFetcher: AnyFetcher = (
    !isLocal && 'useFetcher' in props ? props.useFetcher : useStaticFetcher
  ) as AnyFetcher;

  const {
    data: fetcherData,
    isLoading,
    isFetching,
  } = useActiveFetcher({
    page: pagination.pageIndex + 1,
    perPage: pagination.pageSize,
    orderBy: sorting[0]?.id ?? 'id',
    order: sorting[0]?.desc ? 'desc' : 'asc',
    ...Object.fromEntries(Object.entries(search).filter(([_, v]) => v)),
  } as any);

  const rows = isLocal ? ((props.data as TRow[]) ?? []) : (fetcherData?.data ?? []);

  const table = useReactTable({
    data: rows,
    columns,
    pageCount: isLocal ? undefined : (fetcherData?.pagination?.totalPages ?? 0),
    state: {
      sorting,
      pagination,
      columnVisibility,
      rowSelection,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualSorting: !isLocal,
    manualPagination: !isLocal,
    manualFiltering: !isLocal,
    getCoreRowModel: getCoreRowModel(),
  });

  const totalPages = fetcherData?.pagination?.totalPages ?? 0;
  const totalItems = fetcherData?.pagination?.total ?? 0;
  const currentPage = pagination.pageIndex + 1;

  const startItem = totalItems > 0 ? pagination.pageIndex * pagination.pageSize + 1 : 0;

  const endItem = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalItems);

  return (
    <div className="space-y-4">
      <div className="relative rounded-md border overflow-hidden">
        {isFetching && !isLoading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/60 backdrop-blur-[1px]">
            <Spinner />
          </div>
        )}

        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((group) => (
              <TableRow key={group.id}>
                {group.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={
                      header.column.columnDef.meta?.sticky
                        ? STICKY_COLUMN_CLASSNAME[header.column.columnDef.meta.sticky]
                        : undefined
                    }
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {columns.map((_, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-full rounded bg-muted animate-pulse" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  onDoubleClick={() => onRowDoubleClick?.(row.original)}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={
                        cell.column.columnDef.meta?.sticky
                          ? STICKY_COLUMN_CLASSNAME[cell.column.columnDef.meta.sticky]
                          : undefined
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-muted-foreground">
                    <Icon name="search-x" size={28} />
                    <span>Nenhum resultado encontrado</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {!isLocal && (
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Itens</span>

            <Select
              value={pagination.pageSize.toString()}
              onValueChange={(v) => table.setPageSize(Number(v))}
            >
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>

              <SelectContent>
                <SelectGroup>
                  {[5, 10, 25, 50, 100].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="text-sm text-muted-foreground">
            {startItem}-{endItem} de {totalItems}
          </div>

          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="outline"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className="hover:scale-105 transition"
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>

            <Button
              size="icon"
              variant="outline"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="hover:scale-105 transition"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <span className="text-sm font-medium px-2">
              {currentPage} / {totalPages || 1}
            </span>

            <Button
              size="icon"
              variant="outline"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="hover:scale-105 transition"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>

            <Button
              size="icon"
              variant="outline"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className="hover:scale-105 transition"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

interface SortableHeaderProps<TData> {
  column: Column<TData, unknown>;
  label: string;
}

export function SortableHeader<TData>({ column, label }: SortableHeaderProps<TData>) {
  const sorted = column.getIsSorted();

  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(sorted === 'asc')}
      className="flex items-center gap-1"
    >
      {label}
      {sorted && (
        <Icon name={sorted === 'asc' ? 'arrow-up' : 'arrow-down'} className="h-4 w-4" />
      )}
    </Button>
  );
}

interface ActionsButtonProps {
  id?: any;
  edit?: boolean;
  remove?: boolean;
  view?: boolean;
  onEdit?: (id: any) => void;
  onView?: (id: any) => void;
  onRemove?: (id: any) => void;
  queryKey?: any;
  onRemoveSuccess?: () => void;
  /** Descrição customizada pro modal de confirmação de exclusão — chamada ao abrir o modal. Retornando `undefined` mantém o texto padrão. */
  getRemoveDescription?: (id: any) => Promise<string | undefined>;
  children?: React.ReactNode;
}

export const ActionsButton: React.FC<ActionsButtonProps> = ({
  children,
  edit,
  id,
  remove,
  view,
  onEdit,
  onRemove,
  onView,
  onRemoveSuccess,
  queryKey,
  getRemoveDescription,
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState<boolean>(false);
  const [removeDescription, setRemoveDescription] = useState<string | undefined>(
    undefined,
  );
  const [loadingRemoveDescription, setLoadingRemoveDescription] = useState(false);
  const queryClient = useQueryClient();

  const handleEdit = () => {
    if (onEdit) onEdit(id);
  };

  const handleView = () => {
    if (onView) onView(id);
  };

  const handleConfirmRemove = async () => {
    if (onRemove) {
      await onRemove(id);

      if (queryKey) {
        queryClient.invalidateQueries({
          queryKey: Array.isArray(queryKey) ? queryKey : [queryKey],
        });
      }

      if (onRemoveSuccess) {
        onRemoveSuccess();
      }
    }
    setIsRemoveModalOpen(false);
  };

  const handleRemove = () => {
    setIsRemoveModalOpen(true);
    if (getRemoveDescription) {
      setRemoveDescription(undefined);
      setLoadingRemoveDescription(true);
      getRemoveDescription(id)
        .then(setRemoveDescription)
        .catch(() => setRemoveDescription(undefined))
        .finally(() => setLoadingRemoveDescription(false));
    }
  };

  useEffect(() => {
    const closeOnEscapeKeyDown = (event: KeyboardEvent) => {
      if ((event.key === 'Escape' || event.key === 'Esc') && isRemoveModalOpen) {
        setIsRemoveModalOpen(false);
      }
    };

    document.addEventListener('keydown', closeOnEscapeKeyDown);
    return () => document.removeEventListener('keydown', closeOnEscapeKeyDown);
  }, [isRemoveModalOpen]);

  const removeModal = (
    <AlertDialog onOpenChange={setIsRemoveModalOpen} open={isRemoveModalOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-center">
            Você tem certeza que deseja excluir esses dados?
          </AlertDialogTitle>
          <AlertDialogDescription className="w-full  py-4flex flex-col items-center justify-center">
            <svg
              className="mx-auto mb-4 text-gray-400 w-12 h-12 dark:text-gray-200"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400 text-center">
              {loadingRemoveDescription
                ? 'Verificando dados relacionados...'
                : (removeDescription ?? 'Isso não poderá ser desfeito.')}
            </h3>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <div className="flex w-full gap-4 items-center justify-center">
            <AlertDialogCancel onClick={() => setIsRemoveModalOpen(false)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRemove}
              disabled={loadingRemoveDescription}
            >
              Confirmar
            </AlertDialogAction>
          </div>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return (
    <>
      {removeModal}
      <div className="flex items-center justify-end gap-1">
        {edit ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                icon="pencil"
                size="icon"
                variant="ghost"
                onClick={handleEdit}
                className="cursor-pointer flex items-center text-xs  text-yellow-700 dark:text-yellow-500 transition-all duration-100 hover:text-gray-500 p-1 rounded"
              />
            </TooltipTrigger>
            <TooltipContent>Editar</TooltipContent>
          </Tooltip>
        ) : null}
        {view ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                icon="eye"
                variant="ghost"
                size="icon"
                onClick={handleView}
                className="cursor-pointer flex items-center text-xs  text-blue-700 dark:text-blue-500 transition-all duration-100 hover:text-gray-500 p-1 rounded"
              />
            </TooltipTrigger>
            <TooltipContent>Visualizar</TooltipContent>
          </Tooltip>
        ) : null}
        {remove ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                icon="trash"
                variant="ghost"
                size="icon"
                onClick={handleRemove}
                className="cursor-pointer flex items-center text-xs text-red-700 dark:text-red-500 transition-all duration-100 hover:text-red-500 p-1 rounded"
              />
            </TooltipTrigger>
            <TooltipContent>Excluir</TooltipContent>
          </Tooltip>
        ) : null}
        {children}
      </div>
    </>
  );
};
